import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Login.css';

const configuredApiUrl = process.env.REACT_APP_API_URL;
if (!configuredApiUrl) {
  throw new Error('REACT_APP_API_URL must be configured before starting the admin portal.');
}

const API_BASE_URL = configuredApiUrl;

type ServerState = 'checking' | 'waking' | 'ready' | 'unreachable';

/** FastAPI returns HTTPException detail either flattened or nested; read both. */
function readError(err: any, fallback: string): string {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  return d?.message || d?.detail?.message || fallback;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serverState, setServerState] = useState<ServerState>('checking');
  const [warmupElapsed, setWarmupElapsed] = useState(0);
  const warmupTimer = useRef<NodeJS.Timeout | null>(null);
  // Optional second login step. The backend answers /auth/login with a
  // short-lived challenge for accounts where TOTP applies; holding the token
  // here turns that response into a code prompt rather than a failed login.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const { login, completeMfa, completeMfaWithBackupCode } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const stopTimer = () => { if (warmupTimer.current) { clearInterval(warmupTimer.current); warmupTimer.current = null; } };

    const startTimer = () => {
      const start = Date.now();
      warmupTimer.current = setInterval(() => {
        if (!cancelled) setWarmupElapsed(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    };

    const probe = async () => {
      try {
        await axios.get(`${API_BASE_URL}/healthz`, { timeout: 5000 });
        if (!cancelled) { stopTimer(); setServerState('ready'); }
      } catch {
        if (cancelled) return;
        setServerState('waking');
        startTimer();
        try {
          await axios.get(`${API_BASE_URL}/healthz`, { timeout: 90000 });
          if (!cancelled) { stopTimer(); setServerState('ready'); }
        } catch {
          if (!cancelled) {
            stopTimer();
            // Don't block — server might be up but CORS is blocking the preflight.
            // Let the user try to log in anyway.
            setServerState('ready');
          }
        }
      }
    };

    probe();
    return () => { cancelled = true; stopTimer(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.status === 'mfa_required') {
        setMfaToken(result.mfaToken);
        setLoading(false);
        return;
      }
      navigate('/dashboard');
    } catch (err: any) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Request timed out. The server may be starting up — please wait a moment and try again.');
      } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
        setError('Network error — the server may be blocked by CORS or unavailable. Check the browser console for details.');
      } else {
        setError(readError(err, 'Invalid email or password.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken) return;
    setError('');
    setLoading(true);

    try {
      if (useBackupCode) {
        await completeMfaWithBackupCode(mfaToken, code.trim());
      } else {
        await completeMfa(mfaToken, code.trim());
      }
      navigate('/dashboard');
    } catch (err: any) {
      // The challenge token lives 5 minutes. Once it lapses, re-entering a code
      // can never succeed, so send the user back rather than looping on a
      // "wrong code" message that is not their mistake.
      const expired = err.response?.data?.error_code === 'INVALID_MFA_TOKEN'
        || err.response?.data?.detail?.error_code === 'INVALID_MFA_TOKEN';
      if (expired) {
        setMfaToken(null);
        setCode('');
        setError('That sign-in attempt expired. Please enter your password again.');
      } else {
        setError(readError(err, useBackupCode
          ? 'That backup code was not accepted.'
          : 'That code was not accepted. Codes change every 30 seconds — try the current one.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaToken(null);
    setCode('');
    setUseBackupCode(false);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="bg-shape bg-shape-1" />
        <div className="bg-shape bg-shape-2" />
        <div className="bg-shape bg-shape-3" />
      </div>

      <div className="login-wrapper">
        {/* Left branding panel */}
        <div className="login-branding">
          <div className="brand-content">
            <div className="brand-logo">
              <div className="brand-logo-icon">T</div>
              <span className="brand-logo-text">trinze</span>
            </div>
            <h2 className="brand-headline">Admin Portal</h2>
            <p className="brand-description">
              Manage users, monitor transactions, oversee campaigns, and keep your platform running smoothly.
            </p>
            <div className="brand-features">
              <div className="brand-feature">
                <span className="feature-dot" />
                Real-time analytics & metrics
              </div>
              <div className="brand-feature">
                <span className="feature-dot" />
                Fraud detection & compliance
              </div>
              <div className="brand-feature">
                <span className="feature-dot" />
                Campaign & settlement management
              </div>
            </div>
          </div>
          <div className="brand-footer">
            <span className="brand-version">v0.4.4</span>
          </div>
        </div>

        {/* Right login form */}
        <div className="login-form-panel">
          <div className="login-form-inner">
            <div className="form-header">
              <h1>{mfaToken ? 'Two-factor authentication' : 'Welcome back'}</h1>
              <p>
                {mfaToken
                  ? useBackupCode
                    ? 'Enter one of your saved backup codes'
                    : 'Enter the 6-digit code from your authenticator app'
                  : 'Sign in to your admin account'}
              </p>
            </div>

            {/* Server status — only visible during startup */}
            {serverState === 'checking' && (
              <div className="server-banner checking">
                <span className="spinner-sm" />
                <span>Connecting to server...</span>
              </div>
            )}
            {serverState === 'waking' && (
              <div className="server-banner waking">
                <span className="spinner-sm amber" />
                <div className="banner-text">
                  <span>Server is starting up...</span>
                  <span className="banner-sub">
                    {warmupElapsed < 15
                      ? 'This usually takes 15–30 seconds'
                      : warmupElapsed < 45
                        ? `${warmupElapsed}s elapsed — almost there`
                        : `${warmupElapsed}s — hang tight, cold starts can take a moment`}
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="form-error">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {mfaToken ? (
              <form onSubmit={handleMfaSubmit} className="login-form">
                <div className="field">
                  <label htmlFor="mfa-code">
                    {useBackupCode ? 'Backup code' : 'Authentication code'}
                  </label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                    <input
                      id="mfa-code"
                      // Backup codes are alphanumeric; TOTP codes are 6 digits.
                      // inputMode drives the mobile keypad, so it has to follow the mode.
                      type="text"
                      inputMode={useBackupCode ? 'text' : 'numeric'}
                      value={code}
                      onChange={(e) => setCode(
                        useBackupCode
                          ? e.target.value.trim()
                          : e.target.value.replace(/\D/g, '').slice(0, 6)
                      )}
                      required
                      placeholder={useBackupCode ? 'xxxx-xxxx-xxxx' : '123456'}
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={useBackupCode ? 24 : 6}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || (!useBackupCode && code.length !== 6) || !code}
                  className={`submit-btn ${loading ? 'loading' : ''}`}
                >
                  {loading ? (
                    <>
                      <span className="spinner-sm white" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>

                <div className="mfa-actions">
                  <button
                    type="button"
                    className="mfa-link"
                    onClick={() => { setUseBackupCode(!useBackupCode); setCode(''); setError(''); }}
                  >
                    {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code'}
                  </button>
                  <button type="button" className="mfa-link" onClick={cancelMfa}>
                    Back to sign in
                  </button>
                </div>
              </form>
            ) : (
            <form onSubmit={handleSubmit} className="login-form">
              <div className="field">
                <label htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="M22 7l-10 7L2 7"/>
                  </svg>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@trinze.com"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0110 0v4"/>
                  </svg>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`submit-btn ${loading ? 'loading' : ''}`}
              >
                {loading ? (
                  <>
                    <span className="spinner-sm white" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
